import axios, { AxiosError, isAxiosError } from "axios";
import {
  QueryKey,
  UseQueryOptions,
  useQuery,
  useMutation,
  UseMutationOptions,
  MutationKey,
  queryOptions,
} from "@tanstack/react-query";
import { ErrorResponseBody, Routes } from "@gradience/api-types";
import config from "./config";
import Bugsnag from "@bugsnag/js";

const { VITE_API_URL, VITE_TENANT_ID } = config;

const api = axios.create({
  baseURL: VITE_API_URL,
  headers: {
    "x-gradience-tenant-id": VITE_TENANT_ID,
  },
});

// Add a response interceptor
api.interceptors.response.use(
  (response) => {
    // Any status code within the range of 2xx will cause this function to trigger
    return response;
  },
  (error) => {
    Bugsnag.notify(error);
    return Promise.reject(error);
  }
);

export default api;

const addParamsToPath = <
  TMethod extends keyof Routes[TPath],
  TPath extends keyof Routes,
  TParams extends ExtractParams<Routes[TPath][TMethod]>,
>(
  path: TPath,
  params?: TParams
) => {
  let pathWithParams: string = path;
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      pathWithParams = pathWithParams.replace(
        `:${key}`,
        // Idk why I have to cast -\_(o_o)_/-
        encodeURIComponent(value as string)
      );
    }
  }
  return pathWithParams;
};

export const LOGGED_IN_USER_KEY = ["me"] as const;
export const queryKeys: {
  [TPath in keyof Routes]: QueryKey & MutationKey;
} = {
  "/registration-stats": ["registration-stats"],
  "/school": ["school"],
  "/users": ["users"],
  "/users/:id": ["users"],
  "/setup-intents": ["setup-intents"],
  "/auth/login": LOGGED_IN_USER_KEY,
  "/auth/register": LOGGED_IN_USER_KEY,
  "/auth/verify-email": LOGGED_IN_USER_KEY,
  "/auth/password-reset-request": ["password-reset-request"],
  "/auth/password-reset": LOGGED_IN_USER_KEY,
  "/me": LOGGED_IN_USER_KEY,
  "/manifest": ["manifest"],
  "/auth/verify-email-request": ["verify-email-request"],
  "/tests/:slug": ["tests"],
  "/school-list": ["school-list"],
  "/auth/impersonation-token": ["impersonation-token"],
  "/groups": ["groups"],
  "/groups/:id": ["groups"],
  "/student-lists": ["student-lists"],
  "/students": ["students"],
  "/curricula": ["curricula"],
  "/chapters": ["chapters"],
  "/concept-groups": ["concept-groups"],
  "/number-of-questions": ["number-of-questions"],
  "/auth/admin/login": LOGGED_IN_USER_KEY,
  "/auth/admin/password-reset-request": ["password-reset-request"],
  "/auth/admin/password-reset": LOGGED_IN_USER_KEY,
  "/questions": ["questions"],
  "/questions/:id": ["questions"],
  "/question-pools": ["question-pools"],
  "/concepts": ["concepts"],
  "/chapters/:id": ["chapters"],
  "/curricula/:id": ["curricula"],
  "/chapters/order": ["chapters"],
  "/concepts/:id": ["concepts"],
  "/concept-groups/:id": ["concept-groups"],
  "/supplemental-concept-groups": ["supplemental-concept-groups"],
  "/supplemental-concept-groups/:id": ["supplemental-concept-groups"],
  "/supplemental-concepts/:id": ["supplemental-concept-groups"],
  "/supplemental-concepts": ["supplemental-concept-groups"],
  "/tests": ["tests"],
  "/answers": ["answers"],
  "/test-scans": ["test-scans"],
  "/test-scan-pages": ["test-scan-pages"],
  "/test-dates-overrides": ["test-dates-overrides"],
  "/test-dates-overrides/:id": ["test-dates-overrides"],
  "/grade-tests": ["grade-tests"],
  "/grading-progress": ["grading-progress"],
  "/test-scan-pages/:id/fiducial-annotations": ["fiducial-annotations"],
  "/test-scan-pages/:id": ["test-scan-pages"],
  "/grading-issues": ["grading-issues"],
  "/grading-issues/:id": ["grading-issues"],
  "/test-scan-pages/:id/answer-circle-annotations": [
    "answer-circle-annotations",
  ],
  "/test-scan-pages/:id/partial-answer-circle-annotations": [
    "answer-circle-annotations",
  ],
  "/grading-issues/:id/resolution": ["grading-issue-resolution"],
  "/score-snapshots": ["score-snapshots"],
  "/school/payment-method": ["school-payment-method"],
  "/todo-items": ["todo-items"],
  "/mock-date": ["mock-date"],
  "/students/:id": ["student"],
  "/groups/:id/test-scans": ["group-test-scans"],
  "/groups/:id/test-scans/:testScanId": ["group-test-scan"],
  "/uploads/presigned-url": ["presigned-url"],
  "/groups/:id/test-scan-pages": ["group-test-scan-pages"],
  "/groups/:id/test-scan-pages/bulk-discard": [
    "group-test-scan-pages-bulk-discard",
  ],
  "/groups/:id/test-scan-pages/bulk-identify": [
    "group-test-scan-pages-bulk-identify",
  ],
  "/groups/:id/test-scan-pages/:pageId": ["group-test-scan-page"],
  "/registration-funnel": ["registration-funnel"],
  "/duplicate-data": ["duplicate-data"],
  "/test-scan-pages/:id/questions": ["test-scan-page-questions"],
  "/registrations/:testId": ["test-registration"],
  "/registrations/:testId/payments": ["test-registration-payments"],
  "/registrations/:testId/students": ["test-registration-students"],
};

export type ResponseForPath<
  TPath extends keyof Routes,
  TMethod extends keyof Routes[TPath],
> = Routes[TPath][TMethod] extends {
  response: infer TResponse;
}
  ? TResponse
  : never;

export type UseMutationOptionsForEndpoint<
  TPath extends keyof Routes,
  TMethod extends keyof Routes[TPath],
> = UseMutationOptions<
  ResponseForPath<TPath, TMethod>,
  unknown,
  { body: ExtractBody<Routes[TPath][TMethod]> }
>;

type ExtractBody<T> = T extends { body: infer TBody } ? TBody : {};

export const getToken = () => localStorage.getItem("token");

export const getApi = async <
  TPath extends keyof Routes,
  TMethod extends "get" extends keyof Routes[TPath] ? "get" : never,
  TParams extends ExtractParams<Routes[TPath][TMethod]>,
  TQuery extends ExtractQuery<Routes[TPath][TMethod]>,
>(
  path: TPath,
  params: TParams,
  query?: TQuery
) => {
  const token = getToken();

  const res = await api.get<ResponseForPath<TPath, TMethod>>(
    addParamsToPath<TMethod, TPath, TParams>(path, params),
    {
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {},
      params: query,
    }
  );

  return res.data;
};

export type ExtractParams<T> = T extends { params: infer TParams }
  ? TParams
  : {};

export type ExtractQuery<T> = T extends { query: infer TQuery } ? TQuery : {};

export const isApiError = (
  error: unknown
): error is AxiosError<ErrorResponseBody> => {
  return isAxiosError(error) && error.response?.data?.errors;
};

export const useApiQuery = <
  TPath extends keyof Routes,
  TMethod extends "get" extends keyof Routes[TPath] ? "get" : never,
  TParams extends ExtractParams<Routes[TPath][TMethod]>,
>(
  path: TPath,
  params: TParams,
  options?: Partial<UseQueryOptions<ResponseForPath<TPath, TMethod>, unknown>>,
  query?: ExtractQuery<Routes[TPath][TMethod]>
) =>
  useQuery<ResponseForPath<TPath, TMethod>, unknown>({
    queryKey: [...queryKeys[path], params, query],
    queryFn: async () => {
      return await getApi(path, params, query);
    },
    ...options,
  });

type RequestForPath<
  TPath extends keyof Routes,
  TMethod extends keyof Routes[TPath],
> = Routes[TPath][TMethod] extends { body: infer TRequest } ? TRequest : {};

const putApiOld = async <
  TPath extends keyof Routes,
  TMethod extends "put" extends keyof Routes[TPath] ? "put" : never,
  TParams extends ExtractParams<Routes[TPath][TMethod]>,
>(
  path: TPath,
  body: RequestForPath<TPath, TMethod>,
  params?: TParams
) => {
  const token = getToken();
  const res = await api.put<ResponseForPath<TPath, TMethod>>(
    addParamsToPath(path, params),
    body,
    {
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {},
    }
  );
  return res.data;
};

export const useApiPut = <
  TPath extends keyof Routes,
  TMethod extends "put" extends keyof Routes[TPath] ? "put" : never,
  TParams extends ExtractParams<Routes[TPath][TMethod]>,
>(
  path: TPath,
  params?: TParams,
  options?: UseMutationOptions<
    ResponseForPath<TPath, TMethod>,
    unknown,
    { body: RequestForPath<TPath, TMethod>; params?: TParams }
  >
) => {
  return useMutation<
    ResponseForPath<TPath, TMethod>,
    unknown,
    { body: RequestForPath<TPath, TMethod>; params?: TParams },
    unknown
  >({
    mutationKey: queryKeys[path],
    mutationFn: async ({ body, params: locallyPassedParams }) => {
      return await putApiOld(path, body, locallyPassedParams ?? params);
    },
    ...options,
  });
};

export const postApiOld = async <
  TPath extends keyof Routes,
  TMethod extends "post" extends keyof Routes[TPath] ? "post" : never,
  TParams extends ExtractParams<Routes[TPath][TMethod]>,
>(
  path: TPath,
  body: RequestForPath<TPath, TMethod>,
  params?: TParams
) => {
  const token = getToken();
  const res = await api.post<ResponseForPath<TPath, TMethod>>(
    addParamsToPath(path, params),
    body,
    {
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {},
    }
  );
  return res.data;
};

export const useApiPost = <
  TPath extends keyof Routes,
  TMethod extends "post" extends keyof Routes[TPath] ? "post" : never,
  TParams extends ExtractParams<Routes[TPath][TMethod]>,
>(
  path: TPath,
  params?: TParams,
  options?: UseMutationOptions<
    ResponseForPath<TPath, TMethod>,
    unknown,
    { body: RequestForPath<TPath, TMethod>; params?: TParams }
  >
) => {
  return useMutation<
    ResponseForPath<TPath, TMethod>,
    unknown,
    { body: RequestForPath<TPath, TMethod>; params?: TParams },
    unknown
  >({
    mutationKey: queryKeys[path],
    mutationFn: async ({ body, params: locallyPassedParams }) => {
      return await postApiOld(path, body, locallyPassedParams ?? params);
    },
    ...options,
  });
};

const deleteApiOld = async <
  TPath extends keyof Routes,
  TMethod extends "delete" extends keyof Routes[TPath] ? "delete" : never,
  TParams extends ExtractParams<Routes[TPath][TMethod]>,
>(
  path: TPath,
  body: RequestForPath<TPath, TMethod>,
  params?: TParams
) => {
  const token = getToken();
  const res = await api.delete<ResponseForPath<TPath, TMethod>>(
    addParamsToPath(path, params),
    {
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {},
      data: body,
    }
  );
  return res.data;
};

export const useApiDelete = <
  TPath extends keyof Routes,
  TMethod extends "delete" extends keyof Routes[TPath] ? "delete" : never,
  TParams extends ExtractParams<Routes[TPath][TMethod]>,
>(
  path: TPath,
  params?: TParams,
  options?: UseMutationOptions<
    ResponseForPath<TPath, TMethod>,
    unknown,
    { body: RequestForPath<TPath, TMethod>; params?: TParams }
  >
) => {
  return useMutation<
    ResponseForPath<TPath, TMethod>,
    unknown,
    { body: RequestForPath<TPath, TMethod>; params?: TParams },
    unknown
  >({
    mutationKey: queryKeys[path],
    mutationFn: async ({ body, params: locallyPassedParams }) => {
      return await deleteApiOld(path, body, locallyPassedParams ?? params);
    },
    ...options,
  });
};

type RoutesOfMethod<M extends string> = {
  [P in keyof Routes]: M extends keyof Routes[P] ? P : never;
}[keyof Routes];
type WithOptionalPathParams<T> = [T[keyof T]] extends [never]
  ? { pathParams?: T }
  : { pathParams: T };

type WithOptionalQueryParams<T> = [T[keyof T]] extends [never]
  ? { queryParams?: T }
  : NonNullable<unknown> extends T
    ? { queryParams?: T }
    : { queryParams: T };
export const getQueryOptions = <TPath extends RoutesOfMethod<"get">>(
  opts: { endpoint: TPath } & WithOptionalPathParams<
    ExtractParams<Routes[TPath]["get"]>
  > &
    WithOptionalQueryParams<ExtractQuery<Routes[TPath]["get"]>>
) => {
  const { endpoint, pathParams, queryParams } = opts;

  return queryOptions({
    queryKey: [endpoint, "get", pathParams, queryParams],
    queryFn: async () => {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await api.get<ResponseForPath<TPath, "get">>(
        addParamsToPath(endpoint, pathParams),
        {
          headers,
          params: queryParams,
        }
      );

      return res.data;
    },
  });
};

type WithOptionalBody<T> = [T[keyof T]] extends [never]
  ? { body?: T }
  : NonNullable<unknown> extends T
    ? { body?: T }
    : { body: T };

export const postApi = async <TPath extends RoutesOfMethod<"post">>(
  opts: {
    endpoint: TPath;
  } & WithOptionalPathParams<ExtractParams<Routes[TPath]["post"]>> &
    WithOptionalQueryParams<ExtractQuery<Routes[TPath]["post"]>> &
    WithOptionalBody<ExtractBody<Routes[TPath]["post"]>>
) => {
  const { endpoint, body, pathParams, queryParams } = opts;

  const token = getToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const res = await api.post<ResponseForPath<TPath, "post">>(
    addParamsToPath(endpoint, pathParams),
    body,
    {
      headers,
      params: queryParams,
    }
  );

  return res.data;
};

type PostApiParams<TPath extends RoutesOfMethod<"post">> = {
  endpoint: TPath;
} & WithOptionalPathParams<ExtractParams<Routes[TPath]["post"]>> &
  WithOptionalQueryParams<ExtractQuery<Routes[TPath]["post"]>> &
  WithOptionalBody<ExtractBody<Routes[TPath]["post"]>>;

export function getPostMutationFn<TPath extends RoutesOfMethod<"post">>(
  endpoint: TPath
) {
  return (
    opts: Omit<PostApiParams<TPath>, "endpoint">
  ): Promise<ResponseForPath<TPath, "post">> => {
    // Use a type assertion to assure TypeScript that the merged object
    // fits PostApiParams<TPath>
    const params = { endpoint, ...opts } as PostApiParams<TPath>;
    return postApi(params);
  };
}

export const putApi = async <TPath extends RoutesOfMethod<"put">>(
  opts: {
    endpoint: TPath;
  } & WithOptionalPathParams<ExtractParams<Routes[TPath]["put"]>> &
    WithOptionalQueryParams<ExtractQuery<Routes[TPath]["put"]>> &
    WithOptionalBody<ExtractBody<Routes[TPath]["put"]>>
) => {
  const { endpoint, body, pathParams, queryParams } = opts;

  const token = getToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const res = await api.put<ResponseForPath<TPath, "put">>(
    addParamsToPath(endpoint, pathParams),
    body,
    {
      headers,
      params: queryParams,
    }
  );

  return res.data;
};

export const deleteApi = async <TPath extends RoutesOfMethod<"delete">>(
  opts: {
    endpoint: TPath;
  } & WithOptionalPathParams<ExtractParams<Routes[TPath]["delete"]>> &
    WithOptionalQueryParams<ExtractQuery<Routes[TPath]["delete"]>> &
    WithOptionalBody<ExtractBody<Routes[TPath]["delete"]>>
) => {
  const { endpoint, body, pathParams, queryParams } = opts;

  const token = getToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const res = await api.delete<ResponseForPath<TPath, "delete">>(
    addParamsToPath(endpoint, pathParams),
    {
      headers,
      data: body,
      params: queryParams,
    }
  );

  return res.data;
};
